import React, { ReactElement } from "react";
import moment from "moment";
import { ListTableColumn } from "../../core";

type ColumnKey =
    | "companyName"
    | "createdBy"
    | "createdDateTime"
    | "propertyName"
    | "internalApprovalRequestedBy"
    | "internalApprovalRequestedDateTime"
    | "internalApprovedBy"
    | "internalApprovedDateTime"
    | "clientApprovedBy"
    | "clientApprovedDateTime"
    | "landlordApprovedBy"
    | "landlordApprovedDateTime"
    | "clientSignedBy"
    | "clientSignedDate"
    | "landlordSignedBy"
    | "landlordSignedDate"
    | "clientRejectedBy"
    | "clientRejectedDateTime"
    | "rejectionReason"
    | "version";

const termsheetColumns: Record<ColumnKey, ReactElement> = {
    companyName: (
        <ListTableColumn
            label="Company Name"
            source="leadDetails.company.name"
            column="CompanyName"
            showDefault
            sortable
            settingsGroup="termsheet"
        />
    ),
    createdBy: (
        <ListTableColumn
            label="Created By"
            source="createdBy.name"
            column="createdBy.name"
            showDefault
            sortable
            settingsGroup="termsheet"
        />
    ),
    createdDateTime: (
        <ListTableColumn
            label="Created Date & Time"
            source="document.createdAt"
            column="Created Date & Time"
            showDefault
            formatContent={(d: string | Date | moment.Moment) =>
                moment(d).format("DD MMM YYYY hh:mm A")
            }
            sortable
            settingsGroup="termsheet"
        />
    ),
    propertyName: (
        <ListTableColumn
            label="Property Name"
            source="proposedPropertyDetails"
            column="proposedPropertyDetails"
            formatContent={(data) => (
                <div
                    style={{
                        width: "375px",
                        wordBreak: "break-all",
                        whiteSpace: "normal",
                    }}
                >
                    {data
                        .map((property: any) => property?.propertyDetails?.propertyName)
                        .join(", ")}
                </div>
            )}
            showDefault
            sortable
            settingsGroup="termsheet"
        />
    ),
    internalApprovalRequestedBy: (
        <ListTableColumn
            label="Internal Approval Requested By"
            source="document.internalApproval.requestedBy.name"
            column="document.internalApproval.requestedBy.name"
            showDefault
            sortable
            settingsGroup="termsheet"
        />
    ),
    internalApprovalRequestedDateTime: (
        <ListTableColumn
            label="Internal Approval Requested Date & Time"
            source="document.internalApproval.requestedAt"
            column="document.internalApproval.requestedAt"
            showDefault
            formatContent={(d: string | Date | moment.Moment) =>
                moment(d).format("DD MMM YYYY hh:mm A")
            }
            sortable
            settingsGroup="termsheet"
        />
    ),
    internalApprovedBy: (
        <ListTableColumn
            label="Internal Approved By"
            source="document.internalApproval.approvedBy.name"
            column="document.internalApproval.approvedBy.name"
            showDefault
            sortable
            settingsGroup="termsheet"
        />
    ),
    internalApprovedDateTime: (
        <ListTableColumn
            label="Internal Approved Date & Time"
            source="document.internalApproval.approvedAt"
            column="document.internalApproval.approvedAt"
            showDefault
            formatContent={(d: string | Date | moment.Moment) =>
                moment(d).format("DD MMM YYYY hh:mm A")
            }
            sortable
            settingsGroup="termsheet"
        />
    ),
    clientApprovedBy: (
        <ListTableColumn
            label="Client Approved By"
            source="document.clientApproval.approvedBy.name"
            column="document.clientApproval.approvedBy.name"
            showDefault
            sortable
            settingsGroup="termsheet"
        />
    ),
    clientApprovedDateTime: (
        <ListTableColumn
            label="Client Approved Date & Time"
            source="document.clientApproval.approvedAt"
            column="document.clientApproval.approvedAt"
            showDefault
            formatContent={(d: string | Date | moment.Moment) =>
                moment(d).format("DD MMM YYYY hh:mm A")
            }
            sortable
            settingsGroup="termsheet"
        />
    ),
    landlordApprovedBy: (
        <ListTableColumn
            label="Landlord Approved By"
            source="document.landlordApproval.approvedBy.name"
            column="document.landlordApproval.approvedBy.name"
            showDefault
            sortable
            settingsGroup="termsheet"
        />
    ),
    landlordApprovedDateTime: (
        <ListTableColumn
            label="Landlord Approved Date & Time"
            source="document.landlordApproval.approvedAt"
            column="document.landlordApproval.approvedAt"
            showDefault
            formatContent={(d: string | Date | moment.Moment) =>
                moment(d).format("DD MMM YYYY hh:mm A")
            }
            sortable
            settingsGroup="termsheet"
        />
    ),
    clientSignedBy: (
        <ListTableColumn
            label="Client Signed by"
            source="document.clientApproval.signedBy.fullName"
            column="document.clientApproval.signedBy.fullName"
            showDefault
            sortable
            settingsGroup="termsheet"
        />
    ),
    clientSignedDate: (
        <ListTableColumn
            label="Client Signed Date"
            source="document.clientApproval.signedAt"
            column="document.clientApproval.signedAt"
            showDefault
            formatContent={(d: string | Date | moment.Moment) =>
                moment(d).format("DD MMM YYYY")
            }
            sortable
            settingsGroup="termsheet"
        />
    ),
    landlordSignedBy: (
        <ListTableColumn
            label="Landlord Signed by"
            source="document.landlordApproval.signedBy.fullName"
            column="document.landlordApproval.signedBy.fullName"
            showDefault
            sortable
            settingsGroup="termsheet"
        />
    ),
    landlordSignedDate: (
        <ListTableColumn
            label="Landlord Signed Date"
            source="document.landlordApproval.signedAt"
            column="document.landlordApproval.signedAt"
            showDefault
            formatContent={(d: string | Date | moment.Moment) =>
                moment(d).format("DD MMM YYYY")
            }
            sortable
            settingsGroup="termsheet"
        />
    ),
    clientRejectedBy: (
        <ListTableColumn
            label="Client Rejected by"
            source="document.internalApproval.rejectedBy.name"
            column="document.internalApproval.rejectedBy.name"
            showDefault
            sortable
            settingsGroup="termsheet"
        />
    ),
    clientRejectedDateTime: (
        <ListTableColumn
            label="Client Rejected Date & Time"
            source="document.internalApproval.rejectedAt"
            column="document.internalApproval.rejectedAt"
            showDefault
            formatContent={(d: string | Date | moment.Moment) =>
                moment(d).format("DD MMM YYYY hh:mm A")
            }
            sortable
            settingsGroup="termsheet"
        />
    ),
    rejectionReason: (
        <ListTableColumn
            label="Reason"
            source="document.internalApproval.rejectedReason"
            column="document.internalApproval.rejectedReason"
            showDefault
            sortable
            settingsGroup="termsheet"
        />
    ),
    version: (
        <ListTableColumn
            label="Version"
            source="document.versionNumber"
            column="document.versionNumber"
            showDefault
            sortable
            settingsGroup="termsheet"
        />
    ),
};

const createColumnGroup = (columnKeys: ColumnKey[]) =>
    columnKeys.map((key) => termsheetColumns[key]);

export const DraftColumns = createColumnGroup([
    "companyName",
    "createdBy",
    "createdDateTime",
    "propertyName",
    "version",
]);

export const InternalApprovalColumns = createColumnGroup([
    "companyName",
    "createdBy",
    "createdDateTime",
    "propertyName",
    "internalApprovalRequestedBy",
    "internalApprovalRequestedDateTime",
    "internalApprovedBy",
    "internalApprovedDateTime",
    "version",
]);

export const ClientApprovalColumns = createColumnGroup([
    "companyName",
    "createdBy",
    "createdDateTime",
    "propertyName",
    "clientApprovedBy",
    "clientApprovedDateTime",
    "version",
]);

export const LandlordApprovalColumns = createColumnGroup([
    "companyName",
    "createdBy",
    "createdDateTime",
    "propertyName",
    "landlordApprovedBy",
    "landlordApprovedDateTime",
    "version",
]);

export const SignedColumns = createColumnGroup([
    "companyName",
    "createdBy",
    "createdDateTime",
    "propertyName",
    "clientSignedBy",
    "clientSignedDate",
    "landlordSignedBy",
    "landlordSignedDate",
    "version",
]);

export const RejectedColumns = createColumnGroup([
    "companyName",
    "createdBy",
    "createdDateTime",
    "propertyName",
    "clientRejectedBy",
    "clientRejectedDateTime",
    "rejectionReason",
    "version",
]);

